import { Button } from "@4uhub/lib4uhub";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import DashboardCustomizeRoundedIcon from "@mui/icons-material/DashboardCustomizeRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { motion } from "framer-motion";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IListComponent,
  IListIDashboard,
} from "../../../../../models/dashboard";
import { Header } from "../DashboardFilters/Header/Header";

const TRANSLATION_PATH = "components.panel.";

interface IDashboardComponentsSelectProps {
  dashboard: IListIDashboard;
  components: IListComponent[];
  initialSelected: string[];
  hasFilters: boolean;
  loading: boolean;
  onBackHandler: () => void;
  onConfirm: (selectedIds: string[]) => void;
}

/**
 * Passo de seleção de componentes ao adicionar um dashboard. Todos começam desmarcados e é
 * obrigatório marcar pelo menos um — os desmarcados são criados com Enable = false
 */
export const DashboardComponentsSelect = memo(
  ({
    dashboard,
    components,
    initialSelected,
    hasFilters,
    loading,
    onBackHandler,
    onConfirm,
  }: IDashboardComponentsSelectProps) => {
    const { t } = useTranslation();

    const [selected, setSelected] = useState<string[]>(initialSelected);

    const allSelected =
      components.length > 0 && selected.length === components.length;

    const toggleHandler = useCallback((id: string, checked: boolean) => {
      setSelected((prev) =>
        checked ? [...prev, id] : prev.filter((s) => s !== id),
      );
    }, []);

    const toggleAllHandler = useCallback(
      (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setSelected(checked ? components.map((c) => c.id) : []);
      },
      [components],
    );

    const confirmHandler = useCallback(() => {
      onConfirm(selected);
    }, [onConfirm, selected]);

    return (
      <motion.div
        key={dashboard.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] }}
      >
        <Header name={dashboard.name} onBack={onBackHandler} />

        <FormControl sx={{ width: "100%", my: 2 }}>
          <FormLabel component="legend">
            {t(TRANSLATION_PATH + "select_components")}
          </FormLabel>

          <FormGroup sx={{ maxHeight: 300, overflow: "auto", mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected}
                  indeterminate={selected.length > 0 && !allSelected}
                  onChange={toggleAllHandler}
                />
              }
              label={t(TRANSLATION_PATH + "select_all")}
              sx={(theme) => ({
                pl: 1,
                pb: 1,
                mb: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              })}
            />

            {components.map((c) => (
              <FormControlLabel
                key={c.id}
                sx={{ pl: 1 }}
                control={
                  <Checkbox
                    checked={selected.includes(c.id)}
                    onChange={(_, checked) => toggleHandler(c.id, checked)}
                  />
                }
                label={c.name}
              />
            ))}
          </FormGroup>

          {selected.length === 0 && (
            <FormHelperText>
              {t(TRANSLATION_PATH + "helper_text")}
            </FormHelperText>
          )}
        </FormControl>

        <Box sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            variant="contained"
            loading={loading}
            disabled={selected.length === 0}
            startIcon={
              hasFilters ? (
                <ArrowForwardRoundedIcon />
              ) : (
                <DashboardCustomizeRoundedIcon />
              )
            }
            onClick={confirmHandler}
          >
            {hasFilters
              ? t(TRANSLATION_PATH + "next")
              : t(TRANSLATION_PATH + "add_dashboard")}
          </Button>
        </Box>
      </motion.div>
    );
  },
);
